import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};

  .content-wrapper {
    width: 100%;
    max-width: 90em;
    margin: 0 auto;
    padding: 0 2em 0 2em;
    display: flex;
    flex-direction: column;
    gap: 0;
    height: 100%;
    min-height: 0;
    overflow: hidden;

    @media (max-width: 768px) {
      padding: 1em;
    }
  }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 14px;
    flex-shrink: 0;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "FISCAL · NOTIFICAÇÕES";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};
          background: transparent !important;
          padding: 0 !important;
          border: none !important;
          opacity: 1 !important;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          ${mono};
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-size: 0.9em;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ========== TABS ========== */
  .tabs-header {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      font-size: 0.85em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover {
        opacity: 1;
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      svg { font-size: 1em; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }
    }
  }

  /* ========== EMBEDDED CONTENT ========== */
  .embedded-content {
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    > div {
      padding: 0;
      max-width: none;
      flex: 1;
      min-height: 0;

      .content-wrapper {
        padding: 0;
        max-width: none;
      }
    }
  }
`;
